import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { usePhoneLayout } from './PhoneLayout';

interface TabsContextValue {
  activeValue: string;
  setActiveValue: (value: string) => void;
  idBase: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<Tabs.${component}> must be rendered inside <Tabs>`);
  }
  return ctx;
}

interface TabsProps {
  children: ReactNode;
  /** Initial active tab value (uncontrolled). */
  defaultValue: string;
  /** Controlled active tab value. When provided, the parent owns the state. */
  value?: string;
  /** Fires whenever the active tab changes (click or keyboard). */
  onValueChange?: (value: string) => void;
  className?: string;
}

/**
 * Accessible tabs (WAI-ARIA APG "Tabs" pattern, automatic activation).
 * Compound API: `<Tabs><Tabs.List><Tabs.Trigger/></Tabs.List><Tabs.Content/></Tabs>`.
 * Controllable via `value`/`onValueChange`; otherwise self-managed from `defaultValue`.
 */
export function Tabs({ children, defaultValue, value, onValueChange, className = '' }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const idBase = useId();
  const activeValue = value !== undefined ? value : internal;
  const setActiveValue = (next: string) => {
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };
  return (
    <TabsContext.Provider value={{ activeValue, setActiveValue, idBase }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  /** Required: names the tab set for assistive technology. */
  'aria-label': string;
  className?: string;
}

/** Width kept clear of the right-edge fade when an active tab is scrolled into view. */
const FADE_PX = 28;

/** Scroll the strip ourselves, remembering where, so the scroll listener can tell it from a user's scroll. */
function setLeft(el: HTMLElement, programmaticLeft: { current: number | null }, left: number) {
  el.scrollLeft = left;
  programmaticLeft.current = el.scrollLeft;
}

/**
 * Story #22197 (BR-MOB-012) — the phone tab strip. Below 768px the strip scrolls sideways with no
 * scrollbar, a right-edge fade while more tabs lie off to the right, and a "More" button that scrolls
 * the strip on to the next tabs; the active tab is scrolled into view (the strip's own scrollLeft, so
 * the page never jumps vertically). 768px and up the fade and More button are hidden and the strip is
 * the same row as before. Only used with the phone layout on.
 */
function PhoneTabsList({ children, ariaLabel, className }: { children: ReactNode; ariaLabel: string; className: string }) {
  const { activeValue } = useTabsContext('List');
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // `reveal` = "keep the active tab in view" is still owed. It is set when the active tab changes and
  // cleared once the user scrolls the strip themselves, so a later re-measure (the More button
  // appearing narrows the strip) re-reveals the tab, but never snaps back over a user's own scroll.
  const reveal = useRef(true);
  const programmaticLeft = useRef<number | null>(null);
  const prevActive = useRef<string | null>(null);

  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (programmaticLeft.current === null || Math.abs(el.scrollLeft - programmaticLeft.current) > 1) {
        reveal.current = false;
      }
      measure();
    };
    measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [measure]);

  // Keep the active tab in view: a deep link to the ninth tab must not open on a strip showing the
  // first three. Re-runs when the strip's overflow state changes, because the More button appearing
  // narrows the strip after the first pass.
  useEffect(() => {
    if (prevActive.current !== activeValue) {
      prevActive.current = activeValue;
      reveal.current = true;
    }
    const el = listRef.current;
    if (!reveal.current || !el || el.scrollWidth <= el.clientWidth) return;
    const tab = el.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!tab) return;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left < el.scrollLeft) {
      setLeft(el, programmaticLeft, left);
    } else if (right > el.scrollLeft + el.clientWidth - FADE_PX) {
      setLeft(el, programmaticLeft, right - el.clientWidth + FADE_PX);
    }
    measure();
  }, [activeValue, canScrollRight, measure]);

  const scrollOn = () => {
    const el = listRef.current;
    if (!el) return;
    reveal.current = false;
    setLeft(el, programmaticLeft, Math.min(el.scrollLeft + Math.max(el.clientWidth - FADE_PX * 2, 80), el.scrollWidth - el.clientWidth));
    measure();
  };

  return (
    <div className="flex items-stretch" data-testid="tabs-strip">
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        data-overflowing={canScrollRight ? 'true' : undefined}
        className={`relative flex min-w-0 flex-1 gap-1 overflow-x-auto border-b border-gray-200
          max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden
          ${canScrollRight ? 'max-md:[mask-image:linear-gradient(90deg,#000_calc(100%-28px),transparent)]' : ''}
          ${className}`}
      >
        {children}
      </div>
      {canScrollRight && (
        <button
          type="button"
          onClick={scrollOn}
          aria-label="More tabs"
          className="min-h-11 shrink-0 border-b border-gray-200 px-3 text-sm font-medium text-indigo-700 hover:text-indigo-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 md:hidden"
        >
          More <span aria-hidden>›</span>
        </button>
      )}
    </div>
  );
}

Tabs.List = function TabsList({ children, 'aria-label': ariaLabel, className = '' }: TabsListProps) {
  const phone = usePhoneLayout();
  if (phone) {
    return (
      <PhoneTabsList ariaLabel={ariaLabel} className={className}>
        {children}
      </PhoneTabsList>
    );
  }
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex gap-1 overflow-x-auto border-b border-gray-200 ${className}`}
    >
      {children}
    </div>
  );
};

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  /**
   * Optional count rendered as a pill after the label. A string is rendered as given, so a panel whose
   * read FAILED can show `?` rather than a 0 that would claim the queue is empty (Pipelines, #20869).
   */
  count?: number | string;
  className?: string;
}

Tabs.Trigger = function TabsTrigger({ value, children, disabled = false, count, className = '' }: TabsTriggerProps) {
  const { activeValue, setActiveValue, idBase } = useTabsContext('Trigger');
  const isActive = activeValue === value;

  // Roving focus / automatic activation: Arrow/Home/End move between enabled tabs.
  // The handler lives on the (focusable) tab button — keydown bubbles here from the
  // focused tab — so the tablist container needn't be focusable.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    const list = event.currentTarget.closest('[role="tablist"]');
    if (!list) return;
    // Enabled tabs in DOM order (filter rather than :disabled selector for jsdom robustness).
    const tabs = Array.from(
      list.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    ).filter((t) => !t.disabled);
    if (tabs.length === 0) return;
    event.preventDefault();
    const current = tabs.findIndex((t) => t === event.currentTarget);
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = current < 0 ? 0 : (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = current < 0 ? tabs.length - 1 : (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    const target = tabs[next];
    target.focus();
    const targetValue = target.dataset.value;
    if (targetValue !== undefined) setActiveValue(targetValue);
  };

  return (
    <button
      type="button"
      role="tab"
      id={`${idBase}-tab-${value}`}
      data-value={value}
      aria-selected={isActive}
      aria-controls={`${idBase}-panel-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={() => setActiveValue(value)}
      onKeyDown={handleKeyDown}
      className={`min-h-11 shrink-0 whitespace-nowrap border-b-2 px-4 text-sm font-medium
        transition-colors duration-150 motion-reduce:transition-none
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-inset
        ${isActive ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'}
        ${disabled ? 'cursor-not-allowed opacity-50 hover:border-transparent hover:text-gray-600' : ''}
        ${className}`}
    >
      {children}
      {count !== undefined && (
        <span
          data-testid="tab-count"
          className="ml-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-indigo-50 px-1.5 text-[11px] text-indigo-700"
        >
          {count}
        </span>
      )}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
  /**
   * Mount this panel's children even while it is not the active tab. Default `false`.
   *
   * <b>Opt in only when the tab's LABEL depends on data only the panel can fetch</b> — a live count
   * on the trigger being the case this exists for. Tabs hide most of a page, so a queue behind an
   * unclicked tab is invisible; a count on the trigger is what stops it accumulating unseen. But a
   * count cannot be reported by a panel that was never mounted, so mount-on-active and a live count
   * are mutually exclusive and the caller has to choose.
   *
   * <b>The cost is real and is the caller's to justify:</b> every kept-mounted panel fetches and
   * polls whether or not anyone is looking at it. Do not reach for this to make tab-switching feel
   * faster — that trades a permanent, per-interval cost for a one-off one, on every viewer.
   *
   * The panel stays `hidden` when inactive either way, so assistive technology and sequential focus
   * skip it exactly as before. This changes what is MOUNTED, never what is exposed.
   */
  keepMounted?: boolean;
}

Tabs.Content = function TabsContent({
  value,
  children,
  className = '',
  keepMounted = false,
}: TabsContentProps) {
  const { activeValue, idBase } = useTabsContext('Content');
  const isActive = activeValue === value;
  return (
    <div
      role="tabpanel"
      id={`${idBase}-panel-${value}`}
      aria-labelledby={`${idBase}-tab-${value}`}
      hidden={!isActive}
      tabIndex={0}
      className={isActive ? `pt-4 focus-visible:outline-none ${className}` : ''}
    >
      {/* Mount-on-active by default: panel content (and any data fetch it triggers) only renders
          when selected. `keepMounted` opts out for the live-count case documented above. `hidden`
          on the wrapper is unaffected, so the a11y contract is identical in both modes. */}
      {isActive || keepMounted ? children : null}
    </div>
  );
};

export interface StatusStripOption {
  value: string;
  label: ReactNode;
  /** Optional count shown after the label. */
  count?: number | string;
}

interface StatusStripProps {
  /** Required: names the group for assistive technology (e.g. "Filter by status"). */
  'aria-label': string;
  options: StatusStripOption[];
  /** The selected option's value. */
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Status filter strip (BR-MOB-004, #22193): a row of mutually exclusive status pills.
 *
 * Below 768px it is ONE horizontally scrolling strip, never wrapped onto several rows (Cases had 13
 * status pills over 6 rows on a 375px phone, so no case showed on the first screen). From 768px up
 * it wraps exactly as the hand-rolled pill row it replaces did, so desktop is unchanged.
 *
 * These are filter toggles, not tabs (there is no tab panel), so the semantics are a labelled
 * `group` of `aria-pressed` buttons rather than a tablist.
 */
export function StatusStrip({ 'aria-label': ariaLabel, options, value, onChange, className = '' }: StatusStripProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`flex flex-wrap gap-1.5 max-md:flex-nowrap max-md:overflow-x-auto max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden max-md:[mask-image:linear-gradient(90deg,#000_calc(100%_-_28px),transparent)] ${className}`.trim()}
    >
      {options.map(option => {
        const active = option.value === value;
        return (
          <button
            key={option.value || 'all'}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`px-3 py-1.5 text-sm rounded-md max-md:shrink-0 max-md:whitespace-nowrap max-md:min-h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${active ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            {option.label}
            {option.count !== undefined && (
              <span className="ml-1.5 text-xs text-gray-500">{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
